import * as XLSX from "xlsx";

// Must match InputTemplate.Columns in bom-scheduler (BomCompare.Services/InputTemplate.cs). The backend
// matches the columns by the headers on row 2 and reads data from row 3.
export const TEMPLATE_HEADERS = [
  "Reference Source SOI Number",
  "Format Number",
  "Code",
  "Environment",
  "Compare With",
  "Previous Run Id",
  "Section To Process",
  "Agile/Oracle ESOI or Work Order",
  "Agile Revision",
  "Plant",
  "Include ECO Details",
  "Enable Post Config Queue",
];

// Fallback for GET environments, same keys as ConfiguratorEnvironments in bom-scheduler
export const DEFAULT_ENVIRONMENTS = [
  { key: "Dev", label: "On-Premise Dev", kind: "OnPrem", canConfigure: true },
  { key: "Prd", label: "On-Premise Prd", kind: "OnPrem", canConfigure: true },
  { key: "Uat", label: "On-Premise UAT", kind: "OnPrem", canConfigure: true },
  { key: "CloudDev", label: "Cloud Dev", kind: "Cloud", canConfigure: true },
  // Saved production BOM of the SOI, only for Compare With
  { key: "PrdDb", label: "Production Database", kind: "Database", canConfigure: false },
  // Read from Agile or Oracle by SOI, no codestring
  { key: "Agile", label: "Agile", kind: "Agile", canConfigure: true },
  { key: "Oracle", label: "Oracle BOM", kind: "Oracle", canConfigure: true },
  { key: "OracleBv", label: "Oracle BOM Viewer", kind: "Oracle", canConfigure: true },
];

// Compare With value to configure only the first environment
export const NO_COMPARISON = "None";

// Compare With value to compare against the raw BOM of an earlier run in the same environment
export const PREVIOUS_RUN = "Previous Run";

// Same values as OraclePlants and AgileEcoDetails in bom-scheduler
export const ORACLE_PLANTS = ["SLP", "SL2", "SL3", "FBO", "STN"];
export const ECO_DETAILS_OPTIONS = ["No", "Only DFT/FAB Comp", "All items"];

// On-premise runs of these SOIs configure only the section in "Section To Process"
export const isSectionOnly = (configurationId) => /_sectonly$/i.test((configurationId || "").trim());

export const findEnvironment = (key, environments = DEFAULT_ENVIRONMENTS) =>
  environments.find((env) => env.key.toLowerCase() === (key || "").toLowerCase());

const isExternal = (env) => env?.kind === "Agile" || env?.kind === "Oracle";
const isInfor = (env) => env?.kind === "OnPrem" || env?.kind === "Cloud";

// Environments a row reads or configures (Compare With None / Previous Run add none)
export const rowSides = (row, environments = DEFAULT_ENVIRONMENTS) =>
  [row.environmentA, row.environmentB].map((key) => findEnvironment(key, environments)).filter(Boolean);

// Format Number and Code are only needed to configure in Infor
export const rowNeedsCodestring = (row, environments) => {
  const sides = rowSides(row, environments);
  return sides.length === 0 || sides.some(isInfor);
};
export const rowUsesAgile = (row, environments) => rowSides(row, environments).some((env) => env.kind === "Agile");
export const rowUsesOracle = (row, environments) => rowSides(row, environments).some((env) => env.kind === "Oracle");
export const rowUsesOracleBv = (row, environments) => rowSides(row, environments).some((env) => env.key === "OracleBv");
// Agile and Oracle read the BOM by SOI (ESOI) and/or work order, no codestring
export const rowUsesExternal = (row, environments) => rowSides(row, environments).some(isExternal);

// Agile is compared with Oracle BOM or Oracle BOM Viewer, Oracle BOM with Oracle BOM Viewer; Infor environments with
// each other and with the order in Agile or Oracle
export const canCompareWith = (environmentA, compareWith, environments = DEFAULT_ENVIRONMENTS) => {
  if (compareWith === NO_COMPARISON) return true;
  const first = findEnvironment(environmentA, environments);
  if (compareWith === PREVIOUS_RUN) return !isExternal(first);
  const second = findEnvironment(compareWith, environments);
  if (!second || second.key === first?.key) return false;
  if (isExternal(first)) return isExternal(second);
  if (isExternal(second)) return isInfor(first);
  return true;
};

// An Infor simulation compared with an order in Agile or Oracle: the order goes in its own field, the
// ConfigurationId only names the simulation
export const rowComparesWithOrder = (row, environments = DEFAULT_ENVIRONMENTS) =>
  isInfor(findEnvironment(row.environmentA, environments)) && isExternal(findEnvironment(row.environmentB, environments));

// "BOM from" (environmentA) and "Compare with" (environmentB) of a row, grouped for the two selects.
// BOM from: simulate a codestring in Infor, or read an order from Agile / Oracle.
export const sourceGroups = (environments = DEFAULT_ENVIRONMENTS) =>
  [
    { label: "Simulate a codestring in Infor", options: environments.filter((env) => isInfor(env) && env.canConfigure !== false) },
    { label: "Read an order", options: environments.filter((env) => isExternal(env) && env.canConfigure !== false) },
  ].filter((group) => group.options.length);

// Compare with: only what can be compared with the BOM from, plus nothing (only get the BOM).
// { key, label } options; Agile with the Oracle BOM Viewer also reads the Oracle BOM (three-way).
export const compareGroups = (environmentA, environments = DEFAULT_ENVIRONMENTS) => {
  const allowed = (key) => key !== environmentA && canCompareWith(environmentA, key, environments);
  const first = findEnvironment(environmentA, environments);
  const option = (env) => ({
    key: env.key,
    label: first?.kind === "Agile" && env.key === "OracleBv" ? `${env.label} (+ Oracle BOM)` : env.label,
  });
  const pick = (test) => environments.filter((env) => test(env) && allowed(env.key)).map(option);
  return [
    { label: "Simulate in Infor too", options: pick(isInfor) },
    {
      label: "A saved BOM",
      options: [...pick((env) => env.kind === "Database"), ...(allowed(PREVIOUS_RUN) ? [{ key: PREVIOUS_RUN, label: "Previous run (one of yours)" }] : [])],
    },
    { label: "An order in Agile or Oracle", options: pick(isExternal) },
    { label: "Nothing", options: [{ key: NO_COMPARISON, label: "Only get the BOM" }] },
  ].filter((group) => group.options.length);
};

// What a row of two orders does, shown under Compare with
export const orderPairHint = (environmentA, environmentB, environments = DEFAULT_ENVIRONMENTS) => {
  const first = findEnvironment(environmentA, environments);
  if (!isExternal(first)) return null;
  const keys = [environmentA, environmentB].map((key) => findEnvironment(key, environments)?.key);
  const has = (key) => keys.includes(key);
  if (environmentB === NO_COMPARISON) return `Only reads the BOM of the order from ${first.label}, nothing is compared.`;
  if (has("Agile") && has("OracleBv")) {
    return "Compares Agile, the current Oracle BOM and the Oracle BOM Viewer snapshot of the work order in the same run, like the desktop tool.";
  }
  if (has("Agile")) return "Compares the released Agile BOM with the current Oracle BOM, always the latest version.";
  return "Shows what changed in Oracle since the work order snapshot: the current Oracle BOM against the Oracle BOM Viewer.";
};

// Compare With to switch to when the Environment change makes the current one invalid
export const defaultCompareWith = (environmentA, environments = DEFAULT_ENVIRONMENTS) => {
  const first = findEnvironment(environmentA, environments);
  if (first?.kind === "Agile") return "Oracle";
  if (first?.kind === "Oracle") return "Agile";
  return first?.key === "Prd" ? "Dev" : "Prd";
};

// Short explanation shown under the Environment and Compare With selects, null = nothing to add
export const environmentHint = (key, environments = DEFAULT_ENVIRONMENTS) => {
  if (key === PREVIOUS_RUN) return "Compares against the Raw BOM of one of your earlier runs, so it isn't generated from the simulator again.";
  if (key === NO_COMPARISON) return "Only generates the Raw BOM, nothing is compared.";
  const env = findEnvironment(key, environments);
  if (env?.kind === "Cloud") return "Infor Cloud currently supports format 980 only.";
  if (env?.kind === "Database") {
    return "Takes the latest BOM saved in the production database for this SOI, so it isn't generated from the simulator again.";
  }
  // Agile and Oracle are only offered in Compare With of a simulation
  if (env?.kind === "Agile") return "Compares the simulation with the released BOM of the order in Agile, read with your Agile account. Items are matched by bubble.";
  if (env?.key === "OracleBv") return "Compares the simulation with the Oracle BOM Viewer snapshot of the order's work order. Items are matched by bubble.";
  if (env?.kind === "Oracle") return "Compares the simulation with the current Oracle BOM of the order, always the latest version. Items are matched by bubble.";
  return null;
};

// Outputs of the earlier run in a comparison use the environment key with this prefix, e.g. PrevDev
const PREVIOUS_PREFIX = "Prev";

export const environmentLabel = (key, environments = DEFAULT_ENVIRONMENTS) => {
  const find = (value) => environments.find((env) => env.key.toLowerCase() === (value || "").toLowerCase())?.label;
  const label = find(key);
  if (label) return label;
  const previous = key?.startsWith(PREVIOUS_PREFIX) && find(key.slice(PREVIOUS_PREFIX.length));
  return previous ? `Previous ${previous}` : key;
};

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Builds the upload file for the manual inputs.
// rows: [{ configurationId, formatId, codeString, environmentA, environmentB, previousRunId, sectionToProcess,
//          plant, includeEcoDetails, enablePostConfigQueue }]
// For Agile and Oracle, configurationId is the ESOI or the work order: the API finds the other one.
// previousRunId is only used with environmentB = PREVIOUS_RUN, empty = the latest run.
// Fields that don't apply to the row are left empty, the API rejects them otherwise.
export const buildTemplateFile = (rows, fileName) => {
  const dataRows = rows.map((row) => {
    const needsCodestring = rowNeedsCodestring(row);
    const usesOracle = rowUsesOracle(row);
    return [
      row.configurationId,
      needsCodestring ? row.formatId : "",
      needsCodestring ? row.codeString : "",
      row.environmentA,
      row.environmentB || NO_COMPARISON,
      row.environmentB === PREVIOUS_RUN ? row.previousRunId || "" : "",
      isSectionOnly(row.configurationId) ? row.sectionToProcess || "" : "",
      rowComparesWithOrder(row) ? row.order || "" : "",
      rowUsesAgile(row) ? row.agileRevision || "" : "",
      usesOracle ? row.plant || "" : "",
      usesOracle && rowUsesAgile(row) ? row.includeEcoDetails || "" : "",
      needsCodestring && row.enablePostConfigQueue ? "YES" : "NO",
    ];
  });

  const worksheet = XLSX.utils.aoa_to_sheet([["BOM Compare manual inputs"], TEMPLATE_HEADERS, ...dataRows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Inputs");
  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  return new File([buffer], fileName, { type: XLSX_MIME });
};

export const downloadFile = (file) => {
  const url = window.URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
